import ModalSurface from '../shared/ModalSurface';
import '../../styles/restScreens.css';
import React, { useState, useEffect } from 'react';
import { WIDGET_TYPES } from './widgetRegistry';
import FormulaConfigModal from './FormulaConfigModal';

const TOGGLE_ON_CLASS = 'bg-[var(--btn-fill)]';
const TOGGLE_OFF_CLASS = 'bg-[var(--surface-border)]';

/**
 * Enhanced Widget Configuration Modal
 * Comprehensive modal for creating and configuring widgets
 * Supports widget creation, type selection, custom formulas, and settings
 */
const WidgetConfigModal = ({ widget, isOpen, onClose, onSave, mode = 'edit', theme = 'light' }) => {
  // Mode can be 'create' or 'edit'
  const isCreateMode = mode === 'create' || !widget;

  const [config, setConfig] = useState({
    // Widget Properties
    title: widget?.config?.title || '',
    type: widget?.type || 'kpi_card',
    description: widget?.config?.description || '',

    // Custom Formula
    hasCustomFormula: widget?.config?.hasCustomFormula || false,
    formula: widget?.config?.formula || '',

    // Widget Settings
    clickable: widget?.settings?.clickable ?? true,
    static: widget?.settings?.static ?? false,
    canResize: widget?.settings?.canResize ?? true,
    canRelocate: widget?.settings?.canRelocate ?? true,
    locked: widget?.settings?.locked ?? false,
  });

  // State for Formula popup
  const [isFormulaModalOpen, setIsFormulaModalOpen] = useState(false);

  useEffect(() => {
    if (widget) {
      setConfig({
        title: widget?.config?.title || '',
        type: widget?.type || 'kpi_card',
        description: widget?.config?.description || '',
        hasCustomFormula: widget?.config?.hasCustomFormula || false,
        formula: widget?.config?.formula || '',
        clickable: widget?.settings?.clickable ?? true,
        static: widget?.settings?.static ?? false,
        canResize: widget?.settings?.canResize ?? true,
        canRelocate: widget?.settings?.canRelocate ?? true,
        locked: widget?.settings?.locked ?? false,
      });
    }
  }, [widget]);

  const handleSave = () => {
    const updatedWidget = {
      ...(widget || {}),
      type: config.type,
      config: {
        ...(widget?.config || {}),
        title: config.title,
        description: config.description,
        hasCustomFormula: config.hasCustomFormula,
        formula: config.hasCustomFormula ? config.formula : ''
      },
      settings: {
        clickable: config.clickable,
        static: config.static,
        canResize: config.canResize,
        canRelocate: config.canRelocate,
        locked: config.locked
      }
    };

    onSave(updatedWidget);
    onClose();
  };

  const handleFormulaToggle = () => {
    if (!config.hasCustomFormula) {
      // Enabling formula - open popup
      setIsFormulaModalOpen(true);
      setConfig({ ...config, hasCustomFormula: true });
    } else {
      // Disabling formula - clear it
      setConfig({ ...config, hasCustomFormula: false, formula: '' });
    }
  };

  const handleFormulaSave = (formula) => {
    setConfig({ ...config, formula, hasCustomFormula: true });
  };

  const widgetTypes = [
    { value: 'kpi_card', label: 'KPI Card', icon: '📊' },
    { value: 'revenue_chart', label: 'Revenue Chart', icon: '💰' },
    { value: 'client_chart', label: 'Client Chart', icon: '👥' },
    { value: 'recent_activity', label: 'Recent Activity', icon: '📋' },
    { value: 'quick_actions', label: 'Quick Actions', icon: '⚡' },
    { value: 'calendar_preview', label: 'Calendar Preview', icon: '📅' },
    { value: 'financial_summary', label: 'Financial Summary', icon: '💵' },
    { value: 'expense_chart', label: 'Expense Chart', icon: '📉' },
    { value: 'formula_builder', label: 'Formula Builder', icon: '🧮' },
  ];

  if (!isOpen) return null;

  return (
    <>
    <ModalSurface closeOnEscape={false} onClose={onClose} label="Configure widget" panelProps={{"data-m6-layout":"widgets", "data-m6-component":"WidgetConfigModal"}} panelClassName="rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col bg-[var(--surface-raised)]">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[var(--surface-border)]">
          <div>
            <h3 className="text-xl font-bold text-[var(--ink)]">
              {isCreateMode ? 'Create New Widget' : 'Configure Widget'}
            </h3>
            <p className="text-sm mt-1 text-[var(--ink-muted)]">
              {isCreateMode ? 'Customize your widget before adding it to the dashboard' : 'Update widget properties and settings'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="ia-modal-desktop-close p-2 rounded-lg transition-colors text-[var(--ink-muted)] hover:text-[var(--ink)] hover:bg-[var(--surface-sunken)]"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Widget Properties Section */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold uppercase tracking-wide text-[var(--ink-muted)]">
              Widget Properties
            </h4>

            {/* Widget Type (only for create mode or customizable widgets) */}
            {isCreateMode && (
              <div>
                <label className="block text-sm font-medium mb-2 text-[var(--ink)]">
                  Widget Type
                </label>
                <select
                  value={config.type}
                  onChange={(e) => setConfig({ ...config, type: e.target.value })}
                  className="w-full px-4 py-2.5 text-sm rounded-lg border transition-colors bg-[var(--surface-sunken)] border-[var(--surface-border)] text-[var(--ink)] focus:border-[var(--accent-ink)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-ink)]/20"
                >
                  {widgetTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.icon} {type.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Widget Title */}
            <div>
              <label className="block text-sm font-medium mb-2 text-[var(--ink)]">
                Widget Title
              </label>
              <input
                type="text"
                value={config.title}
                onChange={(e) => setConfig({ ...config, title: e.target.value })}
                placeholder="Enter widget title..."
                className="w-full px-4 py-2.5 text-sm rounded-lg border transition-colors bg-[var(--surface-sunken)] border-[var(--surface-border)] text-[var(--ink)] placeholder-[var(--ink-muted)] focus:border-[var(--accent-ink)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-ink)]/20"
              />
            </div>

            {/* Widget Description */}
            <div>
              <label className="block text-sm font-medium mb-2 text-[var(--ink)]">
                Description (Optional)
              </label>
              <textarea
                value={config.description}
                onChange={(e) => setConfig({ ...config, description: e.target.value })}
                placeholder="Enter widget description..."
                rows={2}
                className="w-full px-4 py-2.5 text-sm rounded-lg border transition-colors resize-none bg-[var(--surface-sunken)] border-[var(--surface-border)] text-[var(--ink)] placeholder-[var(--ink-muted)] focus:border-[var(--accent-ink)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-ink)]/20"
              />
            </div>
          </div>

          {/* Custom Formula Section */}
          <div className="space-y-3 p-4 rounded-lg border border-[var(--surface-border)] bg-[var(--surface-sunken)]">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-semibold text-[var(--ink)]">
                  Custom Formula
                </h4>
                <p className="text-xs mt-0.5 text-[var(--ink-muted)]">
                  Enable custom calculations and formulas
                </p>
              </div>
              <button
                onClick={handleFormulaToggle}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  config.hasCustomFormula ? TOGGLE_ON_CLASS : TOGGLE_OFF_CLASS
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    config.hasCustomFormula ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Formula Status & Edit Button (shown when formula exists) */}
            {config.hasCustomFormula && config.formula && (
              <div className="mt-3 p-3 rounded-lg bg-[var(--surface-raised)]">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium mb-1 text-[var(--ink)]">
                      Current Formula:
                    </div>
                    <div className="text-xs font-mono truncate text-[var(--ui-positive)]">
                      {config.formula}
                    </div>
                  </div>
                  <button
                    onClick={() => setIsFormulaModalOpen(true)}
                    className="px-3 py-1.5 text-xs font-medium rounded-lg transition-colors flex-shrink-0 bg-[var(--nav-active-bg)] text-[var(--nav-active-ink)] hover:brightness-95"
                  >
                    Edit Formula
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Widget Settings Section (shown in both create and edit modes) */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold uppercase tracking-wide text-[var(--ink-muted)]">
              Widget Settings
            </h4>

              <div className="space-y-3">
                {/* Make Clickable */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-[var(--surface-sunken)]">
                  <div>
                    <div className="font-medium text-sm text-[var(--ink)]">
                      Make Clickable
                    </div>
                    <div className="text-xs mt-0.5 text-[var(--ink-muted)]">
                      Allow interaction with widget content
                    </div>
                  </div>
                  <button
                    onClick={() => setConfig({ ...config, clickable: !config.clickable })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      config.clickable ? TOGGLE_ON_CLASS : TOGGLE_OFF_CLASS
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        config.clickable ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* Make Static */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-[var(--surface-sunken)]">
                  <div>
                    <div className="font-medium text-sm text-[var(--ink)]">
                      Make Static
                    </div>
                    <div className="text-xs mt-0.5 text-[var(--ink-muted)]">
                      Prevent dragging and resizing
                    </div>
                  </div>
                  <button
                    onClick={() => setConfig({ ...config, static: !config.static })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      config.static ? TOGGLE_ON_CLASS : TOGGLE_OFF_CLASS
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        config.static ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* Can Resize */}
                <div className={`flex items-center justify-between p-3 rounded-lg bg-[var(--surface-sunken)] ${
                  config.static || config.locked ? 'opacity-50' : ''
                }`}>
                  <div>
                    <div className="font-medium text-sm text-[var(--ink)]">
                      Can Resize
                    </div>
                    <div className="text-xs mt-0.5 text-[var(--ink-muted)]">
                      Allow widget to be resized
                    </div>
                  </div>
                  <button
                    onClick={() => !config.static && !config.locked && setConfig({ ...config, canResize: !config.canResize })}
                    disabled={config.static || config.locked}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      config.canResize && !config.static && !config.locked ? TOGGLE_ON_CLASS : TOGGLE_OFF_CLASS
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        config.canResize && !config.static && !config.locked ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* Can Relocate */}
                <div className={`flex items-center justify-between p-3 rounded-lg bg-[var(--surface-sunken)] ${
                  config.static || config.locked ? 'opacity-50' : ''
                }`}>
                  <div>
                    <div className="font-medium text-sm text-[var(--ink)]">
                      Can Relocate
                    </div>
                    <div className="text-xs mt-0.5 text-[var(--ink-muted)]">
                      Allow widget to be moved
                    </div>
                  </div>
                  <button
                    onClick={() => !config.static && !config.locked && setConfig({ ...config, canRelocate: !config.canRelocate })}
                    disabled={config.static || config.locked}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      config.canRelocate && !config.static && !config.locked ? TOGGLE_ON_CLASS : TOGGLE_OFF_CLASS
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        config.canRelocate && !config.static && !config.locked ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* Lock Widget */}
                <div className="flex items-center justify-between p-3 rounded-lg border bg-[var(--ui-moderate)]/10 border-[var(--ui-moderate)]/30">
                  <div>
                    <div className="font-medium text-sm flex items-center gap-2 text-[var(--ui-moderate)]">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                      </svg>
                      Lock Widget
                    </div>
                    <div className="text-xs mt-0.5 text-[var(--ui-moderate)]">
                      Lock widget in place until unlocked
                    </div>
                  </div>
                  <button
                    onClick={() => setConfig({ ...config, locked: !config.locked })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      config.locked ? 'bg-[var(--ui-moderate)]' : TOGGLE_OFF_CLASS
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        config.locked ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-[var(--surface-border)]">
          <button
            onClick={onClose}
            className="px-5 py-2.5 text-sm font-medium rounded-lg border transition-colors text-[var(--ink)] bg-[var(--surface-raised)] border-[var(--surface-border)] hover:bg-[var(--surface-sunken)]"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-5 py-2.5 text-sm font-medium rounded-lg transition-colors shadow-lg bg-[var(--btn-fill)] text-[var(--btn-ink)] hover:bg-[var(--btn-fill-hover)]"
          >
            {isCreateMode ? 'Create Widget' : 'Save Changes'}
          </button>
        </div>
    </ModalSurface>

      {/* Formula Configuration Popup */}
      <FormulaConfigModal
        isOpen={isFormulaModalOpen}
        onClose={() => setIsFormulaModalOpen(false)}
        onSave={handleFormulaSave}
        initialFormula={config.formula}
        theme={theme}
      />
    </>
  );
};

export default WidgetConfigModal;
